import { useState, useCallback } from 'react'
import { useCreateContractContext, useUserLanguage } from '@/customHooks'
import { NormalFormDateField, NormalFormTextField, SquareButtonsCard } from '../molecules'
import CanCelIcon from '@/assets/contract-icons/cancel-icon.svg'
import ConfirmIcon from '@/assets/contract-icons/confirm-icon.svg'
import textsData from '@/textData/CreateContractsTexts.json'
import { CreateRentChargeInput, RentChargeTypeOption } from '@/graphql/generated'

export const SelectContractSecurityDeposit = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es.securityDeposit : textsData.en.securityDeposit

  const [isFormHidden, setIsFormHidden] = useState(true)

  const {
    state: contractState,
    setSecurityDeposit,
    setRentCharges,
    setButtonEnabled,
    setButtonDisabled,
  } = useCreateContractContext()

  const buttons = [
    { text: texts.button.first, icon: ConfirmIcon },
    { text: texts.button.second, icon: CanCelIcon },
  ];

  const handleDataChange = useCallback((field: 'date' | 'amount', value: string | null) => {
    const rawAmount = field === 'amount' ? value : contractState.depositAmount?.toString() ?? '0';
    const currentAmount = parseInt(rawAmount || '0') || 0;
    
    const currentDateStr = field === 'date' ? value : (contractState.depositReceivedDate ? new Date(contractState.depositReceivedDate).toISOString() : new Date().toISOString());
    const currentDate = currentDateStr ? new Date(currentDateStr) : new Date();

    // 1. Set Security Deposit in Contract State
    setSecurityDeposit(currentAmount, currentDate);

    // 2. Create Rent Charge
    const newRentCharge: CreateRentChargeInput = {
      amountValue: currentAmount.toString(),
      description: texts.chargeDescription,
      executionDate: currentDate,
      rentChargeType: RentChargeTypeOption.SecurityDeposit,
    }

    // 3. Add to Rent Charges in Context
    const currentCharges = contractState.rentCharges || []
    const filteredCharges = currentCharges.filter(
      (charge) => charge.rentChargeType !== RentChargeTypeOption.SecurityDeposit
    )
    setRentCharges([...filteredCharges, newRentCharge])
  }, [contractState.depositAmount, contractState.depositReceivedDate, contractState.rentCharges, setSecurityDeposit, setRentCharges, texts.chargeDescription])

  const onSelectedHandle = useCallback((value: number | null) => {
    switch (value) {
      case null:
        setButtonDisabled()
        break

      case 0:
        setIsFormHidden(false)
        setButtonEnabled()
        // Initialize with default values
        handleDataChange('amount', contractState.depositAmount?.toString() ?? '0')
        break
      
      case 1: {
        setIsFormHidden(true)
        setSecurityDeposit(0)
        // Remove existing security deposit charges if any
        const filteredCharges = (contractState.rentCharges || []).filter(
          (charge) => charge.rentChargeType !== RentChargeTypeOption.SecurityDeposit
        )
        setRentCharges(filteredCharges)
        setButtonEnabled()
        break
      }
      
      default:
        throw new Error('Select value for Security Deposit options is invalid [SelectContractSecurityDeposit]')
    }
  }, [contractState.depositAmount, contractState.rentCharges, handleDataChange, setButtonDisabled, setButtonEnabled, setRentCharges, setSecurityDeposit])

  return (
    <div className="flex flex-col items-center w-full">
      <div className="flex justify-center w-full">
        <SquareButtonsCard
          buttons={buttons}
          onSelected={onSelectedHandle}
        />
      </div>

      <div className={`mt-12 flex flex-col items-center w-full ${isFormHidden ? 'hidden' : ''}`}>
        <form className="bg-white rounded-[18px] p-8 shadow-[0_5px_10px_rgba(0,0,0,0.1)] w-full max-w-[700px]">
          <NormalFormDateField
            label={texts.form.date.label}
            name={texts.form.date.name}
            required
            value={contractState.depositReceivedDate ? new Date(contractState.depositReceivedDate).toISOString() : new Date().toISOString()}
            onChange={(value) => handleDataChange('date', value)}
          />
          <NormalFormTextField
            label={texts.form.amount.label}
            name={texts.form.amount.name}
            required
            value={contractState.depositAmount?.toString() !== '0' ? contractState.depositAmount?.toString() : ''}
            onChange={(value) => handleDataChange('amount', value)}
          />
        </form>
      </div>
    </div>
  );
};
